import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    faPlus,
    faEdit,
    faTrash,
    faChevronLeft,
    faChevronRight,
    faDownload,
    faUpload,
    faRobot,
    faInbox
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import { format } from "date-fns";
import ReactPaginate from "react-paginate";
import GenerateAIQuestions from "./GenerateAIQuestions";
import { PostData, PutData, DeleteData } from '../../../services/FetchData';
import Path from '../../../services/Path';
import { ProductContext } from '../../../store';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import './AIAssistantProducts.scss';


const AIAssistantQuestions = () => {
    const navigate = useNavigate();
    const { currentWebsiteId } = useContext(ProductContext);
    const [pageLoader, setPageLoader] = useState(true);
    const [questions, setQuestions] = useState([]);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage] = useState(10);
    const [openDropdowns, setOpenDropdowns] = useState({});
    const [deleteModal, setDeleteModal] = useState(false);
    const [importModal, setImportModal] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [searchTerm, setSearchTerm] = useState(""); // Input field value (not applied until Apply Filters is clicked)
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(""); // Actual search term used in API calls
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showGenerateAI, setShowGenerateAI] = useState(false);

    // Fetch questions from API
    useEffect(() => {
        const fetchQuestions = async () => {
            const websiteId = currentWebsiteId || Cookies.get("_website_id");
            
            if (!websiteId) {
                console.error("No website ID found");
                toast.error("Website ID is missing. Please refresh the page.");
                setPageLoader(false);
                return;
            }

            setPageLoader(true);
            try {
                const postData = {
                    websiteId: websiteId,
                    page: currentPage + 1, // API expects 1-based page
                    limit: itemsPerPage,
                    searchTerm: appliedSearchTerm || ''
                };

                console.log("Fetching questions with data:", postData);
                
                const response = await PostData("ms1", "api/v1/aiassistant/listquestions", postData);
                
                console.log("API Response:", response);
                
                if (response !== "Invalid" && response !== undefined) {
                    if (response.status === "success") {
                        const questionsData = response.data.questions || [];
                        // Parse date strings to Date objects
                        const formattedQuestions = questionsData.map(q => ({
                            ...q,
                            createdAt: q.createdAt ? new Date(q.createdAt) : new Date(),
                            updatedAt: q.updatedAt ? new Date(q.updatedAt) : new Date()
                        }));
                        setQuestions(formattedQuestions);
                        setTotalQuestions(response.data.total || 0);
                    } else if (response.status === "failure" || response.status === "not_found_error") {
                        console.error("Failed response:", response);
                        const errorMessage = response.message?.message || response.message || "Failed to fetch questions";
                        toast.error(errorMessage);
                        setQuestions([]);
                        setTotalQuestions(0);
                    } else {
                        console.error("Unexpected response format:", response);
                        toast.error(response.message?.message || "Failed to fetch questions");
                        setQuestions([]);
                        setTotalQuestions(0);
                    }
                } else {
                    console.error("Invalid response:", response);
                    toast.error("Invalid response from server");
                    setQuestions([]);
                    setTotalQuestions(0);
                }
            } catch (error) {
                console.error("Error fetching questions:", error);
                toast.error("Failed to fetch questions from API");
                setQuestions([]);
                setTotalQuestions(0);
            } finally {
                setPageLoader(false);
            }
        };

        fetchQuestions();
    }, [currentPage, itemsPerPage, appliedSearchTerm, currentWebsiteId]);

    const toggleDropdown = (questionId) => {
        setOpenDropdowns(prev => ({
            ...prev,
            [questionId]: !prev[questionId]
        }));
    };

    const handleEditClick = (questionId) => {
        const question = questions.find(q => q.id === questionId);
        if (question) {
            // Store question data in localStorage for the form component
            localStorage.setItem('editingQuestion', JSON.stringify(question));
            navigate('/aiassistant/questions/edit');
        }
    };

    const handleAddQuestion = () => {
        // Clear any existing question data
        localStorage.removeItem('editingQuestion');
        navigate('/aiassistant/questions/add');
    };

    const handleDeleteClick = (questionId) => {
        setSelectedQuestion(questionId);
        setDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!selectedQuestion) {
            toast.error("Question ID is missing");
            return;
        }

        const websiteId = currentWebsiteId || Cookies.get("_website_id");
        
        if (!websiteId) {
            toast.error("Website ID is missing");
            return;
        }

        console.log("Deleting question - ID:", selectedQuestion, "WebsiteId:", websiteId);

        try {
            // Ensure questionId is a string
            const questionIdStr = String(selectedQuestion);
            
            const response = await DeleteData(
                "ms1", 
                `api/v1/aiassistant/deletequestion?questionId=${questionIdStr}`, 
                { 
                    websiteId: websiteId,
                    questionId: questionIdStr
                }
            );
            
            console.log("Delete response:", response);

            if (response !== "Invalid" && response !== undefined) {
                if (response.status === "success") {
                    toast.success(response.message || "Question deleted successfully");
                    
                    // Calculate new page - if current page becomes empty, go to previous page
                    const newTotal = totalQuestions - 1;
                    const maxPage = Math.ceil(newTotal / itemsPerPage) - 1;
                    const adjustedPage = currentPage > maxPage && maxPage >= 0 ? maxPage : currentPage;
                    
                    // Refresh the questions list
                    const postData = {
                        websiteId: websiteId,
                        page: adjustedPage >= 0 ? adjustedPage + 1 : 1,
                        limit: itemsPerPage,
                        searchTerm: appliedSearchTerm || ''
                    };
                    
                    const refreshResponse = await PostData("ms1", "api/v1/aiassistant/listquestions", postData);
                    if (refreshResponse !== "Invalid" && refreshResponse.status === "success") {
                        const questionsData = refreshResponse.data.questions || [];
                        const formattedQuestions = questionsData.map(q => ({
                            ...q,
                            createdAt: q.createdAt ? new Date(q.createdAt) : new Date(),
                            updatedAt: q.updatedAt ? new Date(q.updatedAt) : new Date()
                        }));
                        setQuestions(formattedQuestions);
                        setTotalQuestions(refreshResponse.data.total || 0);
                        
                        // Update current page if needed
                        if (adjustedPage !== currentPage) {
                            setCurrentPage(adjustedPage >= 0 ? adjustedPage : 0);
                        }
                    }
                } else {
                    toast.error(response.message?.message || response.message || "Failed to delete question");
                }
            } else {
                toast.error("Invalid response from server");
            }
        } catch (error) {
            console.error("Error deleting question:", error);
            toast.error("Failed to delete question");
        } finally {
            setDeleteModal(false);
            setSelectedQuestion(null);
        }
    };

    // Handler for clear all filters
    const handleClearFilters = () => {
        setSearchTerm('');
        setAppliedSearchTerm('');
        setCurrentPage(0);
        setIsFilterOpen(false);
    };

    // Handler for apply filters
    const handleApplyFilters = () => {
        const trimmedSearchTerm = searchTerm.trim();
        setAppliedSearchTerm(trimmedSearchTerm);
        setCurrentPage(0);
        setIsFilterOpen(false);
    };

    // No need for client-side filtering as API handles it
    //const currentQuestions = questions;
    const pageCount = Math.ceil(totalQuestions / itemsPerPage);

    const handlePageClick = (event) => {
        setCurrentPage(event.selected);
    };

    const handleExportCSV = async () => {
        const websiteId = currentWebsiteId || Cookies.get("_website_id");
        
        if (!websiteId) {
            toast.error("Website ID is missing");
            return;
        }

        try {
            // Call backend export API which returns CSV file
            const response = await fetch(
                `${Path("ms1")}api/v1/aiassistant/exportquestions`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${Cookies.get("_cb_admin_token")}`
                    },
                    body: JSON.stringify({
                        websiteId: websiteId,
                        searchTerm: appliedSearchTerm || ''
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            // Get CSV content from response
            const csvContent = await response.text();

            // Extract filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `product_questions_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            toast.success('Questions exported successfully');
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error('Failed to export CSV file. Please try again.');
        }
    };

    const handleImportCSV = () => {
        setImportModal(true);
    };

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
    };

    const handleImportSubmit = async () => {
        if (!selectedFile) {
            toast.error('Please select a CSV file');
            return;
        }

        const websiteId = currentWebsiteId || Cookies.get("_website_id");
        
        if (!websiteId) {
            toast.error("Website ID is missing");
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const csv = e.target.result;
                
                // Helper function to parse CSV line with proper handling of quoted fields
                const parseCSVLine = (line) => {
                    const result = [];
                    let current = '';
                    let inQuotes = false;
                    
                    for (let i = 0; i < line.length; i++) {
                        const char = line[i];
                        
                        if (char === '"') {
                            if (inQuotes && line[i + 1] === '"') {
                                // Escaped quote
                                current += '"';
                                i++;
                            } else {
                                // Toggle quote state
                                inQuotes = !inQuotes;
                            }
                        } else if (char === ',' && !inQuotes) {
                            // End of field
                            result.push(current.trim());
                            current = '';
                        } else {
                            current += char;
                        }
                    }
                    // Add last field
                    result.push(current.trim());
                    return result;
                };

                const lines = csv.split('\n').filter(line => line.trim());
                if (lines.length === 0) {
                    toast.error('CSV file is empty');
                    return;
                }

                const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim().replace(/"/g, ''));
                
                // Validate required columns (case-insensitive)
                const hasQuestion = headers.some(h => h === 'question');
                const hasSku = headers.some(h => h === 'sku');
                
                if (!hasQuestion || !hasSku) {
                    toast.error('CSV must contain "question" and "sku" columns');
                    return;
                }

                const questionsToImport = [];
                for (let i = 1; i < lines.length; i++) {
                    if (lines[i].trim()) {
                        const values = parseCSVLine(lines[i]);
                        const questionIndex = headers.findIndex(h => h === 'question');
                        const skuIndex = headers.findIndex(h => h === 'sku');
                        const answerIndex = headers.findIndex(h => h === 'answer');
                        const isActiveIndex = headers.findIndex(h => h === 'is_active' || h === 'isactive');
                        
                        const question = {
                            question: values[questionIndex] ? values[questionIndex].replace(/^"|"$/g, '') : '',
                            answer: answerIndex >= 0 && values[answerIndex] ? values[answerIndex].replace(/^"|"$/g, '') : '',
                            sku: values[skuIndex] ? values[skuIndex].replace(/^"|"$/g, '') : '',
                            isActive: isActiveIndex >= 0 && values[isActiveIndex] 
                                ? (values[isActiveIndex].toLowerCase() === 'true' || values[isActiveIndex] === '1')
                                : true
                        };
                        
                        // Only add if question and sku are not empty
                        if (question.question && question.sku) {
                            questionsToImport.push(question);
                        }
                    }
                }

                if (questionsToImport.length === 0) {
                    toast.error('No valid questions found in CSV file');
                    return;
                }

                // Send to backend API
                const response = await PostData(
                    "ms1",
                    "api/v1/aiassistant/importquestions",
                    {
                        websiteId: websiteId,
                        questions: questionsToImport
                    }
                );

                if (response !== "Invalid" && response !== undefined) {
                    if (response.status === "success") {
                        const importedCount = response.data?.imported || questionsToImport.length;
                        const errorCount = response.data?.errors?.length || 0;
                        
                        if (errorCount > 0) {
                            toast.warning(`Imported ${importedCount} questions. ${errorCount} errors occurred.`);
                            if (response.data.errors && response.data.errors.length > 0) {
                                console.warn("Import errors:", response.data.errors);
                            }
                        } else {
                            toast.success(`Successfully imported ${importedCount} questions`);
                        }
                        
                        setImportModal(false);
                        setSelectedFile(null);
                        
                        // Refresh the questions list
                        const postData = {
                            websiteId: websiteId,
                            page: currentPage + 1,
                            limit: itemsPerPage,
                            searchTerm: appliedSearchTerm || ''
                        };
                        const refreshResponse = await PostData("ms1", "api/v1/aiassistant/listquestions", postData);
                        if (refreshResponse !== "Invalid" && refreshResponse.status === "success") {
                            const questionsData = refreshResponse.data.questions || [];
                            const formattedQuestions = questionsData.map(q => ({
                                ...q,
                                createdAt: q.createdAt ? new Date(q.createdAt) : new Date(),
                                updatedAt: q.updatedAt ? new Date(q.updatedAt) : new Date()
                            }));
                            setQuestions(formattedQuestions);
                            setTotalQuestions(refreshResponse.data.total || 0);
                        }
                    } else {
                        toast.error(response.message?.message || response.message || "Failed to import questions");
                    }
                } else {
                    toast.error("Invalid response from server");
                }
            } catch (error) {
                console.error('Error importing questions:', error);
                toast.error('Error importing questions. Please check the CSV format.');
            }
        };
        reader.readAsText(selectedFile);
    };

    const handleAskAI = () => {
        setShowGenerateAI(true);
    };

    const handleBackToQuestions = () => {
        setShowGenerateAI(false);
    };

    // Function to refresh the questions list
    const refreshQuestionsList = async () => {
        const websiteId = currentWebsiteId || Cookies.get("_website_id");
        
        if (!websiteId) {
            return;
        }

        try {
            const postData = {
                websiteId: websiteId,
                page: currentPage + 1,
                limit: itemsPerPage,
                searchTerm: appliedSearchTerm || ''
            };
            
            const response = await PostData("ms1", "api/v1/aiassistant/listquestions", postData);
            
            if (response !== "Invalid" && response !== undefined && response.status === "success") {
                const questionsData = response.data.questions || [];
                const formattedQuestions = questionsData.map(q => ({
                    ...q,
                    createdAt: q.createdAt ? new Date(q.createdAt) : new Date(),
                    updatedAt: q.updatedAt ? new Date(q.updatedAt) : new Date()
                }));
                setQuestions(formattedQuestions);
                setTotalQuestions(response.data.total || 0);
            }
        } catch (error) {
            console.error("Error refreshing questions list:", error);
        }
    };

    const handleToggleActive = async (questionId) => {
        const websiteId = currentWebsiteId || Cookies.get("_website_id");
        
        if (!websiteId) {
            toast.error("Website ID is missing");
            return;
        }

        const question = questions.find(q => q.id === questionId);
        if (!question) return;

        const newActiveState = !question.isActive;

        // Optimistically update UI
        setQuestions(prev => prev.map(q => 
            q.id === questionId 
                ? { ...q, isActive: newActiveState, updatedAt: new Date() }
                : q
        ));

        try {
            const questionIdStr = String(questionId);
            // Use togglestatus with query parameter so Network tab shows "togglestatus" instead of question ID
            // Backend needs to add route: router.put("/questions/togglestatus", ...) that reads questionId from query/body
            const response = await PutData(
                "ms1",
                `api/v1/aiassistant/togglestatus?questionId=${questionIdStr}`,
                {
                    websiteId: websiteId,
                    questionId: questionIdStr,
                    isActive: newActiveState
                }
            );

            if (response !== "Invalid" && response !== undefined) {
                if (response.status === "success") {
                    toast.success(`Question ${newActiveState ? 'activated' : 'deactivated'} successfully`);
                } else {
                    // Revert on error
                    setQuestions(prev => prev.map(q => 
                        q.id === questionId 
                            ? { ...q, isActive: !newActiveState }
                            : q
                    ));
                    toast.error(response.message?.message || response.message || "Failed to update question");
                }
            } else {
                // Revert on invalid response
                setQuestions(prev => prev.map(q => 
                    q.id === questionId 
                        ? { ...q, isActive: !newActiveState }
                        : q
                ));
                toast.error("Invalid response from server");
            }
        } catch (error) {
            console.error("Error toggling question status:", error);
            // Revert on error
            setQuestions(prev => prev.map(q => 
                q.id === questionId 
                    ? { ...q, isActive: !newActiveState }
                    : q
            ));
            toast.error("Failed to update question status");
        }
    };

    if (showGenerateAI) {
        return <GenerateAIQuestions onBack={handleBackToQuestions} onRefresh={refreshQuestionsList} />;
    }

    return (
        <React.Fragment>
            <div className="ai-assistant-questions">
                <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <h2 className="mb-1">Product Questions</h2>
                        <p className="text-muted mb-0">
                            Manage your product questions
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            className="filter-btn"
                            color="outline-secondary"
                            onClick={() => {
                                if (!isFilterOpen) {
                                    setSearchTerm(appliedSearchTerm);
                                }
                                setIsFilterOpen(!isFilterOpen);
                            }}
                        >
                            <i className="fa fa-filter"></i>
                            Filter
                        </Button>
                        <Button 
                            color="outline-secondary" 
                            className="d-flex align-items-center questions-export-button"
                            onClick={handleExportCSV}
                        >
                            <FontAwesomeIcon icon={faDownload} className="me-2" />
                            Export CSV
                        </Button>
                        <Button 
                            color="outline-secondary" 
                            className="d-flex align-items-center questions-import-button"
                            onClick={handleImportCSV}
                        >
                            <FontAwesomeIcon icon={faUpload} className="me-2" />
                            Import CSV
                        </Button>
                        <Button 
                            color="outline-secondary" 
                            className="d-flex align-items-center questions-ask-ai-button"
                            onClick={handleAskAI}
                        >
                            <FontAwesomeIcon icon={faRobot} className="me-2" />
                            Ask AI
                        </Button>
                        <Button 
                            color="primary" 
                            className="d-flex align-items-center conversionBox"
                            onClick={handleAddQuestion}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            Add Question
                        </Button>
                    </div>
                </div>

                {pageLoader ? (
                    <DetailSkeleton />
                ) : (
                    <React.Fragment>
                        <div className="table-responsive">
                            <Table className="data-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Question</th>
                                        <th>Answer</th>
                                        <th>SKU</th>
                                        <th>Is Active</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                        <tbody>
                            {questions.length > 0 ? (
                                questions.map((question) => (
                                    <tr key={question.id}>
                                        <td>
                                            <div className="searchItemStatusWrp">
                                                {question.id}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="searchItemStatusWrp question-text-cell">
                                                <div className="text-truncate" title={question.question}>
                                                    {question.question}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="searchItemStatusWrp answer-text-cell">
                                                <div className="text-truncate" title={question.answer}>
                                                    {question.answer}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="searchItemStatusWrp">
                                                {question.sku}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="searchItemStatusWrp">
                                                <div className="form-check form-switch">
                                                    <Input
                                                        className="form-check-input toggle-switch"
                                                        type="checkbox"
                                                        id={`toggle-${question.id}`}
                                                        checked={question.isActive}
                                                        onChange={() => handleToggleActive(question.id)}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <Dropdown
                                                key={question.id}
                                                isOpen={openDropdowns[question.id] || false}
                                                toggle={() => toggleDropdown(question.id)}
                                                direction="down"
                                            >
                                                <DropdownToggle tag="div" className="dropdown-toggle-custom">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 512" fill="#333" width={"18px"} height={"18px"}>
                                                        <path d="M64 360a56 56 0 1 0 0 112 56 56 0 1 0 0-112zm0-160a56 56 0 1 0 0 112 56 56 0 1 0 0-112zM120 96A56 56 0 1 0 8 96a56 56 0 1 0 112 0z" />
                                                    </svg>
                                                </DropdownToggle>
                                                <DropdownMenu>
                                                    <DropdownItem onClick={() => handleEditClick(question.id)}>
                                                        <span className='dropDownItemWrapper'>
                                                            <FontAwesomeIcon icon={faEdit} /> Edit Question
                                                        </span>
                                                    </DropdownItem>
                                                    <DropdownItem onClick={() => handleDeleteClick(question.id)}>
                                                        <span className='dropDownItemWrapper'>
                                                            <FontAwesomeIcon icon={faTrash} /> Delete Question
                                                        </span>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="text-center p-5">
                                        <div className="d-flex flex-column align-items-center">
                                            <FontAwesomeIcon icon={faInbox} style={{ fontSize: '48px', color: '#dee2e6' }} />
                                            <p className="text-muted mt-3 mb-0">No Questions Found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                                </tbody>
                            </Table>
                        </div>
                    </React.Fragment>
                )}

                {totalQuestions > 0 && (
                        <ReactPaginate
                            previousLabel={<FontAwesomeIcon icon={faChevronLeft} />}
                            nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
                            pageCount={pageCount}
                            onPageChange={handlePageClick}
                            containerClassName="pagination justify-content-center"
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="page-item"
                            previousLinkClassName="page-link"
                            nextClassName="page-item"
                            nextLinkClassName="page-link"
                            activeClassName="active"
                            breakLabel="..."
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                        />
                    )}

                {/* Filter Sidebar */}
                {isFilterOpen && (
                    <div className="filter-sidebar-overlay" onClick={() => {
                        setSearchTerm(appliedSearchTerm);
                        setIsFilterOpen(false);
                    }}>
                        <div className="filter-sidebar" onClick={(e) => e.stopPropagation()}>
                            <div className="filter-sidebar-header">
                                <h5 className="filter-sidebar-title">Filter</h5>
                                <Button
                                    className="close-filter-btn"
                                    color="link"
                                    onClick={() => {
                                        setSearchTerm(appliedSearchTerm);
                                        setIsFilterOpen(false);
                                    }}
                                >
                                    <i className="fa fa-times"></i>
                                </Button>
                            </div>

                            <div className="filter-sidebar-content">
                                {/* Search Section */}
                                <div className="filter-section">
                                    <label className="filter-label">Search</label>
                                    <Input
                                        type="text"
                                        placeholder="Search questions, answers, or SKU..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="filter-input"
                                    />
                                </div>
                            </div>

                            <div className="filter-sidebar-footer">
                                <Button
                                    className="clear-all-btn"
                                    color="secondary"
                                    outline
                                    onClick={handleClearFilters}
                                    style={{
                                        flex: 1,
                                        marginRight: '8px'
                                    }}
                                >
                                    Clear All
                                </Button>
                                <Button
                                    className="apply-filters-btn"
                                    color="primary"
                                    onClick={handleApplyFilters}
                                    style={{
                                        flex: 1
                                    }}
                                >
                                    Apply Filters
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} className="delete-confirmation-modal">
                    <ModalHeader toggle={() => setDeleteModal(false)} className="delete-modal-header">
                        Confirm Delete
                    </ModalHeader>
                    <ModalBody>
                        Are you sure you want to delete this question? This action cannot be undone.
                    </ModalBody>
                    <ModalFooter className="delete-modal-footer">
                        <Button color="secondary" outline onClick={() => setDeleteModal(false)} className="delete-cancel-button">
                            Cancel
                        </Button>
                        <Button color="danger" onClick={confirmDelete} className="delete-confirm-button">
                            Delete
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Import CSV Modal */}
                <div className="import-questions-modal-container" style={{ margin: '0 auto' }}>
                <Modal fullscreen="lg" isOpen={importModal} toggle={() => setImportModal(false)} className="import-questions-modal">
                    <ModalHeader toggle={() => setImportModal(false)}>
                        Import product questions
                    </ModalHeader>
                    <ModalBody style={{ display: 'block' }}>
                        <div className="mb-4">
                            <p>
                                Upload a CSV file containing product questions to import. The CSV file must include 'question' and 'sku' columns. 
                                Optionally, you can include an 'is_active' column with 'true' or 'false' values.
                            </p>
                        </div>
                        
                        <div className="mb-4">
                            <div className="d-flex align-items-center">
                                <Label for="csvFile" className="form-label fw-bold me-3 mb-0" style={{ whiteSpace: 'nowrap' }}>CSV File</Label>
                                <Input
                                    type="file"
                                    id="csvFile"
                                    accept=".csv"
                                    onChange={handleFileChange}
                                    className="form-control"
                                />
                            </div>
                            {selectedFile && (
                                <small className="text-muted mt-1 d-block">
                                    Selected: {selectedFile.name}
                                </small>
                            )}
                        </div>

                        <div className="mb-3">
                            <h6 className="fw-bold">Example CSV format:</h6>
                        </div>
                        
                        <div className="mb-3">
                            <div className="bg-light p-3 rounded csv-format-example">
                                <div>question,sku,is_active</div>
                                <div>"What is the size?","PROD123",true</div>
                                <div>"Is this waterproof?","PROD123",true</div>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button 
                            color="secondary" 
                            outline
                            onClick={() => setImportModal(false)}
                            className="modal-cancel-button"
                        >
                            Cancel
                        </Button>
                        <Button color="primary" onClick={handleImportSubmit} className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faUpload} className="me-2" />
                            Import Questions
                        </Button>
                    </ModalFooter>
                </Modal>
                </div>
            </div>
        </React.Fragment>
    );
}

export default AIAssistantQuestions;

